import React from 'react';
import { Button as BaseButton } from '@base-ui/react';
import { cn } from '../utils';
import { Loader2 } from 'lucide-react';

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'ghost' | 'destructive' | 'outline';
  size?: 'sm' | 'md' | 'lg' | 'icon';
  loading?: boolean;
  icon?: React.ElementType;
}

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant = 'primary', size = 'md', loading, icon: Icon, children, ...props }, ref) => {

    const variants = {
      primary: "bg-primary text-black hover:brightness-105 shadow-glow border border-transparent active:scale-95",
      secondary: "bg-white text-zinc-900 border border-zinc-200 hover:bg-zinc-50 shadow-sm active:bg-zinc-100",
      ghost: "bg-transparent text-zinc-500 hover:text-zinc-900 hover:bg-zinc-100 border border-transparent active:bg-zinc-200",
      destructive: "bg-error-50 text-error-600 hover:bg-error-100 border border-error-100 active:bg-error-200",
      outline: "bg-transparent border-2 border-zinc-200 text-zinc-900 hover:border-zinc-300 active:bg-zinc-50"
    };

    const sizes = {
      sm: "h-9 px-3 text-xs rounded-xl",
      md: "h-11 px-5 text-sm rounded-2xl",
      lg: "h-14 px-8 text-base rounded-2xl",
      icon: "h-10 w-10 p-0 rounded-full flex items-center justify-center"
    };

    return (
      <BaseButton
        ref={ref}
        disabled={loading || props.disabled}
        className={cn(
          "inline-flex items-center justify-center gap-2 font-bold transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-primary/50 disabled:opacity-50 disabled:pointer-events-none cursor-pointer",
          variants[variant],
          sizes[size],
          className
        )}
        {...props}
      >
        {loading && <Loader2 className="animate-spin" size={16} />}
        {!loading && Icon && <Icon size={size === 'sm' ? 14 : 18} strokeWidth={2.5} />}
        {children}
      </BaseButton>
    );
  }
);
Button.displayName = "Button";
